'use client';

import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import useModal from 'src/components/Modals/hooks/useModal';
import Button from 'src/components/primitives/Button/Button';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { useDeleteWhitelistAddress } from 'src/hooks/query/useWhitelistAddresses';
import { CHAIN_NAME_MAP } from 'src/constants/funXyzChains';

function SummaryRow({
	label,
	children,
}: {
	label: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex items-center justify-between">
			<span className="typo-body-xs text-text-secondary">{label}</span>
			<span className="typo-body-xs text-text-default">{children}</span>
		</div>
	);
}

export interface AddressBookDeleteModalProps {
	whitelistId: string;
	label: string;
	address: string;
	token: string;
	chainId: string;
}

export default function AddressBookDeleteModal({
	whitelistId,
	label,
	address,
	token,
	chainId,
}: AddressBookDeleteModalProps) {
	const { hide } = useModal();
	const deleteMutation = useDeleteWhitelistAddress();

	const handleDelete = async () => {
		try {
			await deleteMutation.mutateAsync(whitelistId);
			hide();
		} catch {
			// Error handled by mutation onError callback
		}
	};

	const chainName = CHAIN_NAME_MAP[chainId] ?? chainId;

	return (
		<BaseModal.Container onClose={hide}>
			<BaseModal.Title onClose={hide}>Delete Address Book</BaseModal.Title>
			<BaseModal.Body>
				<div className="flex flex-col gap-y-sm rounded-sm border border-stroke-divider bg-input-bg p-lg">
					<SummaryRow label="Label">{label}</SummaryRow>
					<SummaryRow label="Asset">
						{token ? (
							<div className="flex items-center gap-xs">
								<MarketIcon
									marketSymbol={token}
									className="size-lg flex-shrink-0"
								/>
								<span>{token}</span>
							</div>
						) : (
							'Universal'
						)}
					</SummaryRow>
					<div className="flex flex-col gap-1">
						<span className="text-b4 text-text-secondary">Address</span>
						<span className="text-b4 break-all text-text-default">
							{address}
						</span>
					</div>
					<SummaryRow label="Chain">{chainName}</SummaryRow>
				</div>
			</BaseModal.Body>

			<BaseModal.Footer>
				<Button
					size="lg"
					variant="secondary"
					className="flex-1"
					onClick={hide}
					disabled={deleteMutation.isPending}
				>
					Close
				</Button>
				<Button
					size="lg"
					className="flex-1"
					onClick={handleDelete}
					isLoading={deleteMutation.isPending}
				>
					Delete
				</Button>
			</BaseModal.Footer>
		</BaseModal.Container>
	);
}
